import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import { services2, services1, services3, services4 } from '../assets';
import './NetSuiteServices.css';
import CreativeHeroSection from './CreativeHeroSection';
import FAQSection from './FAQSection';

// Industries data - moved outside component to avoid initialization issues
const industries = [
  {
    name: "Retail & E-commerce",
    description: "Streamline operations, manage inventory, enhance customer experiences, and drive growth."
  },
  {
    name: "Finance & Accounting",
    description: "Automate processes, ensure compliance, and gain real-time financial insights for smarter decision-making."
  },
  {
    name: "Manufacturing",
    description: "Optimize manufacturing operations by streamlining production, inventory, and supply chain management."
  },
  {
    name: "Healthcare & Services",
    description: "Streamline billing, compliance, and patient management for improved efficiency and care."
  },
  {
    name: "Wholesale & Distribution",
    description: "Enhance operations by streamlining inventory, orders, and supply chain for greater efficiency."
  },
  {
    name: "IT & Software",
    description: "Streamline operations, automate billing, and scale effortlessly with real-time insights."
  }
];

const NetSuiteServices = () => {
  // Animation controls for sections
  const controls = useAnimation();
  const [heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [industriesRef, industriesInView] = useInView({ threshold: 0.1 });

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    if (heroInView) controls.start("visible");
  }, [controls, heroInView]);

  // Calculate max slides (showing 3 cards at a time from 6 total)
  // const maxSlides = Math.max(1, industries.length - 2); // 6 - 2 = 4 positions (0,1,2,3)

  // Auto-scroll functionality (3 views: 0, 1, 2)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Slider navigation functions (3 views: 0, 1, 2)
  const goToNext = () => {
    setCurrentSlide(prev => (prev + 1) % 3);
  };

  const goToPrev = () => {
    setCurrentSlide(prev => (prev - 1 + 3) % 3);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const slideInFromLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const slideInFromRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const scaleUp = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="bg-white overflow-hidden">
      <Helmet>
        <title>Reliable NetSuite Managed Services for Growing Businesses</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/services" />
        <meta name="description" content="Get top-rated NetSuite managed services in the USA. Spirenest ensures seamless ERP operations, customization, and ongoing support for your business." />
        <meta name="keywords" content="NetSuite managed services, NetSuite support, ERP operations, USA, Spirenest" />
        <meta property="og:title" content="Reliable NetSuite Managed Services for Growing Businesses" />
        <meta property="og:description" content="Get top-rated NetSuite managed services in the USA. Spirenest ensures seamless ERP operations, customization, and ongoing support for your business." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Reliable NetSuite Managed Services for Growing Businesses" />
        <meta name="twitter:description" content="Get top-rated NetSuite managed services in the USA. Spirenest ensures seamless ERP operations, customization, and ongoing support for your business." />
      </Helmet>
      {/* Hero Section */}
      <CreativeHeroSection />

      {/* Why Choose Us Section */}
      <section className="py-5 bg-light">
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <span className="badge bg-primary bg-opacity-10 text-primary mb-3 px-3 py-2 rounded-pill fw-semibold">
                Why Choose Us
              </span>
              <h2 className="fw-bold display-5 mb-3">Delivering Exceptional Value Through Expertise</h2>
              <p className="lead text-muted  flex-grow-1">Our comprehensive NetSuite services are designed to drive your business forward</p>
            </motion.div>

            <Row className="g-4 align-items-stretch">
              <Col lg={3} md={6}>
                <motion.div
                  variants={fadeIn}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                >
                  <Card className="h-100 border-0 service-card overflow-hidden bg-dark">
                    <div className="card-gradient-bg bg-primary bg-opacity-5"></div>
                    <Card.Body className="p-4 position-relative d-flex flex-column">
                      <div className="icon-wrapper d-flex justify-content-center align-items-center mb-4 p-3">
                        <i className="bi bi-lightning-charge-fill text-gradient fs-4"></i>
                      </div>
                      <h5 className="mb-3 fw-semibold text-gradient">Fast & Flexible </h5>
                      <p className="mb-0 text-white lh-base flex-grow-1">
                        Our phased approach ensures faster project completion with minimal business disruptions.
                      </p>
                      <div className="hover-arrow mt-3 text-gradient">
                        <i className="bi bi-arrow-right"></i>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>

              <Col lg={3} md={6}>
                <motion.div
                  variants={fadeIn}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                >
                  <Card className="h-100 border-0 service-card overflow-hidden bg-dark">
                    <div className="card-gradient-bg bg-primary bg-opacity-5"></div>
                    <Card.Body className="p-4 position-relative d-flex flex-column">
                      <div className="icon-wrapper d-flex justify-content-center align-items-center mb-4 p-3">
                        <i className="bi bi-gear-fill text-gradient fs-4"></i>
                      </div>
                      <h5 className="mb-3 fw-semibold text-gradient">Efficient Management</h5>
                      <p className="mb-0 text-white lh-base flex-grow-1">
                        We handle updates, automation, and performance tuning to reduce overhead and boost ROI.
                      </p>
                      <div className="hover-arrow mt-3 text-gradient">
                        <i className="bi bi-arrow-right"></i>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>

              <Col lg={3} md={6}>
                <motion.div
                  variants={fadeIn}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                >
                  <Card className="h-100 border-0 service-card overflow-hidden bg-dark">
                    <div className="card-gradient-bg bg-primary bg-opacity-5"></div>
                    <Card.Body className="p-4 position-relative d-flex flex-column">
                      <div className="icon-wrapper d-flex justify-content-center align-items-center mb-4 p-3">
                        <i className="bi bi-graph-up-arrow text-gradient fs-4"></i>
                      </div>
                      <h5 className="mb-3 fw-semibold text-gradient">Scalable Solutions</h5>
                      <p className="mb-0 text-white lh-base flex-grow-1">
                        Our solutions evolve with your business, ensuring your NetSuite instance grows with you.
                      </p>
                      <div className="hover-arrow mt-3 text-gradient">
                        <i className="bi bi-arrow-right"></i>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>

              <Col lg={3} md={6}>
                <motion.div
                  variants={fadeIn}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                >
                  <Card className="h-100 border-0 service-card overflow-hidden bg-dark">
                    <div className="card-gradient-bg bg-primary bg-opacity-5"></div>
                    <Card.Body className="p-4 position-relative d-flex flex-column">
                      <div className="icon-wrapper d-flex justify-content-center align-items-center mb-4 p-3">
                        <i className="bi bi-arrow-left-right text-gradient fs-4"></i>
                      </div>
                      <h5 className="mb-3 fw-semibold text-gradient">Seamless Integration</h5>
                      <p className="mb-0 text-white lh-base flex-grow-1">
                        We connect NetSuite with your third-party tools for real-time data and smooth operations.
                      </p>
                      <div className="hover-arrow mt-3 text-gradient">
                        <i className="bi bi-arrow-right"></i>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            </Row>


          </motion.div>
        </Container>
      </section>

      {/* Services Section */}
      <section className="py-5 position-relative">
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-light z-index-0" style={{ zIndex: -1 }}></div>
        <Container>
          <motion.div
            ref={servicesRef}
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            {/* Implementation Section */}
            <Row className="align-items-center mb-5 py-5">
              <Col lg={6}>
                <motion.div variants={slideInFromLeft}>
                  <span className="badge bg-primary bg-opacity-10 text-primary mb-3 px-3 py-2 rounded-pill fw-semibold">
                    Implementation
                  </span>
                  <h2 className="fw-bold mb-4">Tailored NetSuite Implementation & Optimization</h2>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Expert in custom NetSuite implementation and optimization</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Follows an agile, phased approach for faster go-live</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Tailors NetSuite to match your functional roles, processes, and KPIs</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Performs gap analysis and documents business processes</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Configures NetSuite for seamless integration and efficiency</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Supports long-term business growth and scalability</span>
                    </li>
                  </ul>
                  <motion.button
                    className="btn btn-primary mt-3 rounded-pill px-4 py-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Learn About Implementation
                  </motion.button>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div variants={slideInFromRight}>
                  <div className="service-image-container">
                    <div className="floating-dots dots-1"></div>
                    <div className="floating-dots dots-2"></div>
                    <img
                      src={services1}
                      alt="NetSuite Implementation"
                      className="img-fluid rounded-4 "
                    />
                    <div className="floating-badge">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      <span>95% Implementation Success Rate</span>
                    </div>
                  </div>
                </motion.div>
              </Col>
            </Row>

            {/* Managed Services Section */}
            <Row className="align-items-center my-5 py-5">
              <Col lg={6} className="order-lg-2">
                <motion.div variants={slideInFromRight}>
                  <span className="badge bg-primary bg-opacity-10 text-primary mb-3 px-3 py-2 rounded-pill fw-semibold">
                    Managed Services
                  </span>
                  <h2 className="fw-bold mb-4">Comprehensive NetSuite Managed Services</h2>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>End-to-end NetSuite support including configuration and data migration</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Real-time integration with third-party applications</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Clean, accurate, and governed data for better reliability</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Secure, up-to-date environment aligned with industry standards</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Optimized data flow and system performance</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Enhanced reporting and business intelligence</span>
                    </li>
                  </ul>
                  <motion.button
                    className="btn btn-primary mt-3 rounded-pill px-4 py-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Explore Managed Services
                  </motion.button>
                </motion.div>
              </Col>
              <Col lg={6} className="order-lg-1">
                <motion.div variants={slideInFromLeft}>
                  <div className="service-image-container">
                    <div className="floating-dots dots-3"></div>
                    <img
                      src={services2}
                      alt="NetSuite Managed Services"
                      className="img-fluid rounded-4 "
                    />
                    <div className="floating-badge badge-2">
                      <i className="bi bi-shield-fill-check text-primary me-2"></i>
                      <span>24/7 Support Available</span>
                    </div>
                  </div>
                </motion.div>
              </Col>
            </Row>

            {/* Security Section */}
            <Row className="align-items-center my-5 py-5">
              <Col lg={6}>
                <motion.div variants={slideInFromLeft}>
                  <span className="badge bg-primary bg-opacity-10 text-primary mb-3 px-3 py-2 rounded-pill fw-semibold">
                    Security & Training
                  </span>
                  <h3 className="fw-bold mb-4">Security, Compliance & Training</h3>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Prioritize data security with strong encryption and compliance</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Comprehensive NetSuite training for your team</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Dedicated user support to improve adoption</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>24/7 support for prompt issue resolution</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Proactive monitoring for efficient operations</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Ongoing guidance to maximize NetSuite's potential</span>
                    </li>
                  </ul>
                  <motion.button
                    className="btn btn-primary mt-3 rounded-pill px-4 py-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Security Details
                  </motion.button>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div variants={slideInFromRight}>
                  <div className="service-image-container">
                    <div className="floating-dots dots-4"></div>
                    <img
                      src={services3}
                      alt="NetSuite Security"
                      className="img-fluid rounded-4 "
                    />
                    <div className="floating-badge badge-3">
                      <i className="bi bi-lock-fill text-success me-2"></i>
                      <span>Enterprise-grade Security</span>
                    </div>
                  </div>
                </motion.div>
              </Col>
            </Row>

            {/* Analytics Section */}
            <Row className="align-items-center my-5 py-5">
              <Col lg={6} className="order-lg-2">
                <motion.div variants={slideInFromRight}>
                  <span className="badge bg-primary bg-opacity-10 text-primary mb-3 px-3 py-2 rounded-pill fw-semibold">
                    Analytics
                  </span>
                  <h3 className="fw-bold mb-4">Advanced Reporting & Analytics</h3>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Customized reports and interactive dashboards</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Integrated data from multiple sources</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Data-driven decision-making with real-time insights</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Seamless access to analytics</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Improved operational efficiency</span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                      <span>Continuous support and optimization</span>
                    </li>
                  </ul>
                  <motion.button
                    className="btn btn-primary mt-3 rounded-pill px-4 py-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    See Analytics Features
                  </motion.button>
                </motion.div>
              </Col>
              <Col lg={6} className="order-lg-1">
                <motion.div variants={slideInFromLeft}>
                  <div className="service-image-container">
                    <div className="floating-dots dots-5"></div>
                    <img
                      src={services4}
                      alt="NetSuite Analytics"
                      className="img-fluid rounded-4 "
                    />
                    <div className="floating-badge badge-4">
                      <i className="bi bi-bar-chart-line-fill text-info me-2"></i>
                      <span>Real-time Insights</span>
                    </div>
                  </div>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Industries Section */}
      <section className="py-5 position-relative" ref={industriesRef}>
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-light z-index-0" style={{ zIndex: -1 }}></div>
        <Container>
          <motion.div
            initial="hidden"
            animate={industriesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <span className="badge bg-primary bg-opacity-10 text-primary mb-3 px-3 py-2 rounded-pill fw-semibold">
                Industries
              </span>
              <h2 className="fw-bold display-5 mb-3">Digitizing Businesses Across Industries</h2>
              <p className="lead text-muted">
                From startups to large enterprises, we provide scalable ERP solutions that adapt to different industries
              </p>
            </motion.div>

            {/* Enhanced Slider Container */}
            <div className="position-relative">
              {/* Navigation Buttons */}
              <button
                className="btn btn-primary rounded-circle position-absolute top-50 start-0 translate-middle-y"
                onClick={goToPrev}
                style={{ 
                  zIndex: 10, 
                  width: '50px', 
                  height: '50px', 
                  left: '-25px',
                  boxShadow: '0 4px 15px rgba(0,123,255,0.3)'
                }}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              
              <button
                className="btn btn-primary rounded-circle position-absolute top-50 end-0 translate-middle-y"
                onClick={goToNext}
                style={{ 
                  zIndex: 10, 
                  width: '50px', 
                  height: '50px', 
                  right: '-25px',
                  boxShadow: '0 4px 15px rgba(0,123,255,0.3)'
                }}
              >
                <i className="bi bi-chevron-right"></i>
              </button>

              {/* Slider Track */}
              <div className="overflow-hidden rounded-4">
                <motion.div
                  ref={sliderRef}
                  className="d-flex transition-all"
                  animate={{ 
                    x: `-${currentSlide * (100 / 3)}%` 
                  }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 300, 
                    damping: 30 
                  }}
                  style={{ 
                    width: `${(industries.length / 2) * 100}%` 
                  }}
                >
                  {industries.map((industry, index) => (
                    <motion.div
                      key={index}
                      className="px-2"
                      style={{ 
                        width: `${100 / industries.length}%`,
                        minWidth: '300px'
                      }}
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Card className={`h-100 border-0 shadow-lg industry-card text-white position-relative overflow-hidden ${
                        index === currentSlide ? 'active-slide' : ''
                      }`} style={{
                        background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
                        minHeight: '280px'
                      }}>
                        {/* Gradient Overlay */}
                        <div 
                          className="position-absolute top-0 start-0 w-100 h-100"
                          style={{
                            background: `linear-gradient(135deg, ${getIndustryColor(industry.name)}20, transparent)`,
                            zIndex: 1
                          }}
                        ></div>
                        
                        <Card.Body className="p-4 position-relative" style={{ zIndex: 2 }}>
                          <div className="d-flex align-items-center justify-content-center mb-4">
                            <div 
                              className="icon-wrapper d-flex align-items-center justify-content-center rounded-circle"
                              style={{
                                width: '80px',
                                height: '80px',
                                background: `linear-gradient(135deg, ${getIndustryColor(industry.name)}, ${getIndustryColor(industry.name)}80)`,
                                boxShadow: `0 8px 25px ${getIndustryColor(industry.name)}40`
                              }}
                            >
                              <i className={`bi bi-${getIndustryIcon(industry.name)} text-white`} style={{ fontSize: '2rem' }}></i>
                            </div>
                          </div>
                          <h5 className="mb-3 text-center fw-bold text-white">{industry.name}</h5>
                          <p className="mb-0 text-center text-white-50 small" style={{ lineHeight: '1.6' }}>
                            {industry.description}
                          </p>
                        </Card.Body>

                        {/* Active slide indicator */}
                        {index === currentSlide && (
                          <div 
                            className="position-absolute bottom-0 start-0 w-100"
                            style={{
                              height: '4px',
                              background: `linear-gradient(90deg, ${getIndustryColor(industry.name)}, transparent)`,
                              zIndex: 3
                            }}
                          ></div>
                        )}
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              {/* Dots Navigation */}
              <div className="d-flex justify-content-center mt-4 gap-2">
                {Array.from({ length: 3 }, (_, index) => (
                  <button
                    key={index}
                    className={`btn rounded-circle p-0 ${
                      index === currentSlide 
                        ? 'btn-primary' 
                        : 'btn-outline-secondary'
                    }`}
                    onClick={() => goToSlide(index)}
                    style={{ 
                      width: '12px', 
                      height: '12px',
                      minWidth: '12px',
                      border: index === currentSlide ? 'none' : '2px solid #dee2e6'
                    }}
                  ></button>
                ))}
              </div>
            </div>
          </motion.div>
        </Container>
      </section>


      {/* Stats Section */}
      {/* <section className="py-5 bg-dark text-white position-relative">
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 z-index-0"></div>
        <Container className="position-relative z-index-1">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="py-5"
          >
            <Row className="g-4">
              <Col lg={3} md={6}>
                <motion.div variants={scaleUp} className="text-center">
                  <div className="display-4 fw-bold mb-2 text-primary">95%</div>
                  <p className="mb-0 text-white-50">Implementation Success Rate</p>
                </motion.div>
              </Col>
              <Col lg={3} md={6}>
                <motion.div variants={scaleUp} className="text-center">
                  <div className="display-4 fw-bold mb-2 text-primary">2.5x</div>
                  <p className="mb-0 text-white-50">Average ROI Increase</p>
                </motion.div>
              </Col>
              <Col lg={3} md={6}>
                <motion.div variants={scaleUp} className="text-center">
                  <div className="display-4 fw-bold mb-2 text-primary">24/7</div>
                  <p className="mb-0 text-white-50">Support Availability</p>
                </motion.div>
              </Col>
              <Col lg={3} md={6}>
                <motion.div variants={scaleUp} className="text-center">
                  <div className="display-4 fw-bold mb-2 text-primary">100+</div>
                  <p className="mb-0 text-white-50">Successful Deployments</p>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section> */}

      {/* CTA Section */}
      <section className="py-5 position-relative overflow-hidden bg-primary">
        <div className="cta-shape-1"></div>
        <div className="cta-shape-2"></div>
        <Container className="position-relative z-index-1">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center py-5"
          >
            <h2 className="fw-bold text-white mb-4 display-5">Ready to Transform Your Business with NetSuite?</h2>
            <p className="lead text-white mb-5">
              Contact us today to discuss how we can help you maximize the value of your NetSuite investment
            </p>
            <div className="d-flex justify-content-center gap-3">
              <motion.div
                whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(0,0,0,0.2)" }}
                whileTap={{ scale: 0.95 }}
              >
                <a href="/contact" className="btn btn-light btn-lg px-4 py-3 rounded-pill fw-semibold text-decoration-none">
                    Get Started Today
                </a>
              </motion.div>
              {/* <motion.button
                className="btn btn-outline-light btn-lg px-4 py-3 rounded-pill fw-semibold"
                whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
                whileTap={{ scale: 0.95 }}
              >
                Schedule Demo
              </motion.button> */}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

// Helper function to get icons for industries
const getIndustryIcon = (industryName) => {
  switch (industryName) {
    case "Retail & E-commerce":
      return "cart";
    case "Finance & Accounting":
      return "currency-dollar";
    case "Manufacturing":
      return "gear";
    case "Healthcare & Services":
      return "heart-pulse";
    case "Wholesale & Distribution":
      return "truck";
    case "IT & Software":
      return "laptop";
    default:
      return "building";
  }
};

// Helper function to get colors for industries
const getIndustryColor = (industryName) => {
  switch (industryName) {
    case "Retail & E-commerce":
      return "#FF6B6B";
    case "Finance & Accounting":
      return "#4ECDC4";
    case "Manufacturing":
      return "#45B7D1";
    case "Healthcare & Services":
      return "#A78BFA";
    case "Wholesale & Distribution":
      return "#F9A826";
    case "IT & Software":
      return "#6C5CE7";
    default:
      return "#007bff";
  }
};

export default NetSuiteServices;